import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import { IoCloseSharp } from "react-icons/io5";
import { warning } from "@alert";
import { confirmPageLeaveIfDirty } from "@helpers/pageLeaveGuard";
import { isAllowed } from "@helpers/checkPermission";
import { CheckoutDetailsPerfectScrollbar } from "@components/CheckoutDetailsScrollbar";
import "@components/CheckoutDetailsScrollbar/checkout-details-scrollbar.scss";
import { useModalPerfectScrollbarResync } from "@hooks/useModalPerfectScrollbarResync";
import useModalSlideClose from "@hooks/useModalSlideClose";
import {
  MODULE_DEFAULT_PATH_CHANGED_EVENT,
  MODULES_WITH_DEFAULT_HOME,
  getSavedModuleDefaultPath,
  hasUserConfiguredModuleDefault,
  persistModuleDefaultPath,
  getModuleDefaultHomeDialogTitle,
  normalizeModulePath,
} from "@helpers/moduleDefaultPath";
import { flattenMenuBarDefaultHomeOptions } from "@helpers/moduleDefaultHomeMenu";
import {
  GROUP_DEFAULT_HOME_CONFIGS,
  getSavedGroupDefaultPath,
  hasUserConfiguredGroupDefault,
  persistGroupDefaultPath,
} from "@helpers/groupDefaultPath";
import "./_module-default-home.scss";

const MODULE_PANEL_KEY = "module";
const MODULE_DEFAULT_HOME_PS_CLASS =
  "module-default-home__scroll checkout-details-ps--main checkout-details-ps--theme-slate-narrow scrollbar-container";

function OptionButton({
  path,
  label,
  hasPermission,
  isSelected,
  onSelect,
  starVariant = "filled",
}) {
  const isDisabled = !hasPermission;
  const StarIcon = starVariant === "outline" ? StarOutlined : StarFilled;
  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-disabled={isDisabled}
      title={isDisabled ? `${label}（無權限無法選擇）` : label}
      className={`module-default-home__option${
        isSelected ? " module-default-home__option--selected" : ""
      }${isDisabled ? " module-default-home__option--disabled" : ""}`}
      onClick={() => onSelect(path, hasPermission)}
    >
      <span>{isDisabled ? `${label}（無權限無法選擇）` : label}</span>
      {isSelected && !isDisabled && (
        <StarIcon
          className={`module-default-home__option-star${
            starVariant === "outline"
              ? " module-default-home__option-star--outline"
              : ""
          }`}
          aria-hidden
        />
      )}
    </button>
  );
}

function ModuleDefaultHomeSelector({ routerName, menuBarList, onDefaultChange }) {
  const navigate = useNavigate();
  const permissions = useSelector((state) => state.user.user.permissions);
  const [isOpen, setIsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(MODULE_PANEL_KEY);
  const [selectedPath, setSelectedPath] = useState("");
  const [hasConfigured, setHasConfigured] = useState(false);
  const [groupSelections, setGroupSelections] = useState({});

  const closeDialog = useCallback(() => setIsOpen(false), []);

  const { closingClassName, shouldRender, requestClose } = useModalSlideClose({
    isOpen,
    onClosed: closeDialog,
  });

  const groupConfigs = useMemo(
    () =>
      GROUP_DEFAULT_HOME_CONFIGS.filter(
        (config) => config.routerName === routerName
      ),
    [routerName]
  );

  const syncFromStorage = useCallback(() => {
    if (!MODULES_WITH_DEFAULT_HOME.has(routerName)) return;
    setHasConfigured(hasUserConfiguredModuleDefault(routerName));
    setSelectedPath(getSavedModuleDefaultPath(routerName) || "");

    const nextGroups = {};
    groupConfigs.forEach((config) => {
      nextGroups[config.key] = {
        hasConfigured: hasUserConfiguredGroupDefault(
          config.routerName,
          config.groupEng
        ),
        path: getSavedGroupDefaultPath(config.routerName, config.groupEng) || "",
      };
    });
    setGroupSelections(nextGroups);
  }, [routerName, groupConfigs]);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage, permissions]);

  useEffect(() => {
    const onChanged = (event) => {
      const key = event.detail?.sidebarName;
      if (key === routerName || String(key || "").startsWith(`${routerName}/`)) {
        syncFromStorage();
      }
    };
    window.addEventListener(MODULE_DEFAULT_PATH_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(MODULE_DEFAULT_PATH_CHANGED_EVENT, onChanged);
  }, [routerName, syncFromStorage]);

  useEffect(() => {
    if (!shouldRender) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      requestClose();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [shouldRender, requestClose]);

  const tabOptions = useMemo(() => {
    return flattenMenuBarDefaultHomeOptions(menuBarList, routerName).map(
      (option) => ({
        ...option,
        hasPermission: isAllowed(option.path, permissions),
      })
    );
  }, [menuBarList, routerName, permissions]);

  const groupSections = useMemo(() => {
    return groupConfigs
      .map((config) => {
        const options = config.paths.map((path) => {
          const normalized = normalizeModulePath(path);
          return {
            path: normalized,
            label: config.pathLabels?.[normalized] || normalized,
            hasPermission: isAllowed(normalized, permissions),
          };
        });
        return {
          ...config,
          options,
          hasAccessibleOptions: options.some((option) => option.hasPermission),
        };
      })
      .filter((section) => section.hasAccessibleOptions);
  }, [groupConfigs, permissions]);

  const panelTabs = useMemo(() => {
    const tabs = [{ key: MODULE_PANEL_KEY, label: "區塊首頁" }];
    groupSections.forEach((section) => {
      tabs.push({ key: section.key, label: section.title });
    });
    return tabs;
  }, [groupSections]);

  const hasAccessibleOptions = tabOptions.some((option) => option.hasPermission);
  const dialogTitle = getModuleDefaultHomeDialogTitle(routerName);
  const showPanelToggle = panelTabs.length > 1;

  const {
    scrollRef,
    contentRef,
    handlePsSync,
    handleScrollY,
  } = useModalPerfectScrollbarResync([activePanel, isOpen, tabOptions.length], {
    enabled: isOpen,
  });

  useEffect(() => {
    if (
      activePanel !== MODULE_PANEL_KEY &&
      !groupSections.some((section) => section.key === activePanel)
    ) {
      setActivePanel(MODULE_PANEL_KEY);
    }
  }, [activePanel, groupSections]);

  const openDialog = () => {
    setActivePanel(MODULE_PANEL_KEY);
    setIsOpen(true);
  };

  const handleSelectModule = (path, hasPermission) => {
    if (!hasPermission || !isAllowed(path, permissions)) {
      warning(1, "權限不足", "您沒有訪問此頁面的權限", "我了解了");
      return;
    }

    persistModuleDefaultPath(routerName, path);
    setSelectedPath(path);
    setHasConfigured(true);
    onDefaultChange?.(path);
    requestClose(() => {
      setIsOpen(false);
      confirmPageLeaveIfDirty(() => navigate(path));
    });
  };

  const handleSelectGroup = (config, path, hasPermission) => {
    if (!hasPermission || !isAllowed(path, permissions)) {
      warning(1, "權限不足", "您沒有訪問此頁面的權限", "我了解了");
      return;
    }

    persistGroupDefaultPath(config.routerName, config.groupEng, path);
    setGroupSelections((prev) => ({
      ...prev,
      [config.key]: { hasConfigured: true, path },
    }));
    onDefaultChange?.(path);
    requestClose(() => {
      setIsOpen(false);
      confirmPageLeaveIfDirty(() => navigate(path));
    });
  };

  if (!MODULES_WITH_DEFAULT_HOME.has(routerName) || !hasAccessibleOptions) {
    return null;
  }

  const activeGroupSection =
    activePanel === MODULE_PANEL_KEY
      ? null
      : groupSections.find((section) => section.key === activePanel) || null;

  return (
    <>
      <button
        type="button"
        className="module-default-home__trigger"
        aria-label={dialogTitle}
        title={dialogTitle}
        onClick={openDialog}
      >
        <span className="module-default-home__bookmark" aria-hidden>
          <StarFilled className="module-default-home__bookmark-star" />
        </span>
      </button>

      {shouldRender &&
        createPortal(
          <div
            className={`module-default-home__backdrop${
              closingClassName ? ` ${closingClassName}` : ""
            }`}
            role="presentation"
            onClick={() => requestClose()}
          >
            <div
              className={`module-default-home__dialog${
                routerName === "shiftschedule"
                  ? " module-default-home__dialog--shiftschedule"
                  : ""
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="module-default-home-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="module-default-home__dialog-header">
                <h2 id="module-default-home-title">{dialogTitle}</h2>
                <button
                  type="button"
                  className="module-default-home__close PopCheckoutDetail-header__close-inline confirm-btn-hover"
                  aria-label="關閉"
                  title="關閉"
                  onClick={() => requestClose()}
                >
                  <IoCloseSharp size={22} aria-hidden />
                </button>
              </div>

              {showPanelToggle && (
                <div
                  className="module-default-home__panel-toggle"
                  role="tablist"
                  aria-label="設定類型"
                >
                  {panelTabs.map((tab, index) => {
                    const isActive = activePanel === tab.key;
                    const isModuleTab = tab.key === MODULE_PANEL_KEY;
                    return (
                      <React.Fragment key={tab.key}>
                        {index === 1 && (
                          <span
                            className="module-default-home__panel-toggle-divider"
                            aria-hidden
                          />
                        )}
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`module-default-home__panel-toggle-btn${
                            isModuleTab
                              ? " module-default-home__panel-toggle-btn--module"
                              : " module-default-home__panel-toggle-btn--group"
                          }${
                            isActive
                              ? " module-default-home__panel-toggle-btn--active"
                              : ""
                          }`}
                          onClick={() => setActivePanel(tab.key)}
                        >
                          {tab.label}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              <p className="module-default-home__hint">
                {activePanel === MODULE_PANEL_KEY
                  ? "選擇進入此區塊的首頁（僅影響您在此瀏覽器的帳號）。"
                  : `選擇點擊「${activeGroupSection?.title || "分組"}」時前往的頁面（僅影響您在此瀏覽器的帳號）。`}
              </p>

              <CheckoutDetailsPerfectScrollbar
                ref={scrollRef}
                className={MODULE_DEFAULT_HOME_PS_CLASS}
                minOverflowYPx={96}
                onSync={handlePsSync}
                onScrollY={handleScrollY}
              >
                <div ref={contentRef}>
                  {activePanel === MODULE_PANEL_KEY ? (
                    <ul className="module-default-home__options">
                      {tabOptions.map((option) => {
                        const isSelected =
                          hasConfigured && selectedPath === option.path;
                        return (
                          <li key={option.path}>
                            <OptionButton
                              path={option.path}
                              label={option.label}
                              hasPermission={option.hasPermission}
                              isSelected={isSelected}
                              starVariant="filled"
                              onSelect={handleSelectModule}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  ) : activeGroupSection ? (
                    <ul className="module-default-home__options">
                      {activeGroupSection.options.map((option) => {
                        const selection =
                          groupSelections[activeGroupSection.key] || {};
                        const isSelected =
                          selection.hasConfigured &&
                          selection.path === option.path;
                        return (
                          <li key={option.path}>
                            <OptionButton
                              path={option.path}
                              label={option.label}
                              hasPermission={option.hasPermission}
                              isSelected={isSelected}
                              starVariant="outline"
                              onSelect={(path, hasPermission) =>
                                handleSelectGroup(
                                  activeGroupSection,
                                  path,
                                  hasPermission
                                )
                              }
                            />
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              </CheckoutDetailsPerfectScrollbar>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default ModuleDefaultHomeSelector;
